<script lang="ts">
  import { onMount } from 'svelte';
  import type { TicketUser } from '../../lib/types';
  import { gotoTicket } from '../router';
  import Notice from '../ui/Notice.svelte';
  import LoadingBlock from '../ui/LoadingBlock.svelte';

  const API_BASE = import.meta.env.DEV ? 'http://localhost:3001' : '';

  let searchTerm = '';
  let loading = true;
  let error = '';
  let users: TicketUser[] = [];

  onMount(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/tickets`, { credentials: 'include' });
      if (response.status === 401) {
        error = 'Admin login required. Visit ?admin=true to sign in.';
        return;
      }
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.error ?? response.statusText ?? `HTTP ${response.status}`);
      }
      const data = await response.json();
      users = Array.isArray(data) ? data : [];
    } catch (err) {
      console.error('Error loading user list', err);
      error = err instanceof Error ? err.message : 'Could not load user list.';
    } finally {
      loading = false;
    }
  });

  $: filtered = searchTerm
    ? users.filter(user => user.person?.name?.toLowerCase().includes(searchTerm.toLowerCase()))
    : users;
</script>

<div class="linkfinder container">
  <h1>Link Finder</h1>
  <input type="text" bind:value={searchTerm} placeholder="Search by name…" />

  {#if loading}
    <LoadingBlock label="Loading tickets…" />
  {:else if error}
    <Notice variant="error">{error}</Notice>
  {:else if filtered.length === 0}
    <p class="linkfinder__empty">No tickets match "{searchTerm}".</p>
  {:else}
    <ul>
      {#each filtered as user (user.id)}
        <li>
          <button type="button" on:click={() => gotoTicket(user.eventId, user.id, { push: true })}>
            {user.person?.name ?? 'Unknown'} · {user.id} · {user.eventId}
          </button>
        </li>
      {/each}
    </ul>
  {/if}
</div>

<style>
  .linkfinder {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding: var(--space-6) var(--space-4);
    max-width: var(--content-max-width);
  }

  .linkfinder h1 {
    margin: 0;
  }

  .linkfinder input {
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: var(--color-surface-900);
    color: var(--color-text-primary);
    font-size: var(--font-size-base);
  }

  .linkfinder ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .linkfinder li button {
    width: 100%;
    text-align: left;
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: var(--color-surface-850);
    color: var(--color-text-primary);
    cursor: pointer;
    font-size: var(--font-size-sm);
  }

  .linkfinder li button:hover {
    border-color: rgb(var(--accent-rgb) / 0.4);
  }

  .linkfinder__empty {
    color: var(--color-text-muted);
  }
</style>
